import React, { useEffect, useState } from 'react';
import DsButton from '../../common/DsButton';
import DsSelect from '../../common/DsSelect';
import { useToast } from '../../common/ToastSystem';
import {
  EplusCandidateResult,
  EplusPerformanceCandidate,
  EventTicketOption,
  linkAccountPlayguidePerformance,
  listAccountPlayguidePerformanceCandidates,
  listAvailableAccountPlayguidePerformanceCandidates,
} from '../../../api/playguides';
import { jstDatetimeLocalToUtcIso } from '../../../utils/datetime';

const Select = DsSelect as unknown as React.ComponentType<any>;
type ToastApi = { success: (message: string) => void; error: (message: string) => void };

type Props = {
  guideCode?: 'eplus' | 'pia' | 'lawson';
  eventUuid?: string;
  eventPerformances?: Array<{
    performanceIndex: number;
    performanceName: string;
    performanceDate: string;
    startTime?: string;
  }>;
  performanceScoped?: boolean;
  eventTickets: Array<EventTicketOption & {
    ticketIndex?: number;
    eventPerformanceIndex?: number | null;
  }>;
  onDraftChange?: (draft: EplusSourceDraft | null) => void;
  onSaved: () => void | Promise<void>;
};

export type EplusSourceDraft = {
  casticartPerformanceIndex: number | null;
  connectionId: number;
  externalPerformanceId: number;
  ticketMappings: Array<{
    externalTicketKey: string;
    targetMode: 'auto' | 'existing' | 'create' | 'ignore';
    casticartTicketId?: number | null;
    targetTicketIndex?: number | null;
    configuredCapacity: number;
    targetPerformanceMode: 'event' | 'performance';
    casticartPerformanceId?: number | null;
    targetPerformanceIndex?: number | null;
  }>;
  siteFee: number;
  salesScheduleMode: 'event' | 'custom';
  salesStartAt: string | null;
  salesEndAt: string | null;
};

const yen = new Intl.NumberFormat('ja-JP', { style: 'currency', currency: 'JPY' });
const normalizeTicketLabel = (value: unknown) => String(value || '')
  .normalize('NFKC')
  .replace(/[\s\u3000・･/_-]+/gu, '')
  .toLocaleLowerCase('ja-JP');
const receptionScopedTicketName = (seatType: unknown, receptionName: unknown) => {
  const seat = String(seatType || '').normalize('NFKC').trim() || '券種';
  const reception = String(receptionName || '').normalize('NFKC').trim();
  return reception && normalizeTicketLabel(seat) !== normalizeTicketLabel(reception)
    ? `${seat}（${reception}）`
    : seat;
};

const performanceKey = (performance: EplusCandidateResult['performances'][number]) =>
  performance.performanceId == null
    ? `index:${performance.performanceIndex ?? 0}`
    : `id:${performance.performanceId}`;

export default function EplusTicketLinkPanel({
  guideCode = 'eplus',
  eventUuid,
  eventPerformances = [],
  performanceScoped = false,
  eventTickets,
  onDraftChange,
  onSaved,
}: Props) {
  const guideLabel = guideCode === 'pia'
    ? 'チケットぴあ'
    : guideCode === 'lawson'
      ? 'ローチケ'
      : 'イープラス';
  const guideTimingNotice = guideCode === 'pia'
    ? 'チケットぴあは販売開始前には連動できません。販売開始後、次回の取得完了後（通常は翌朝。管理者が「今すぐ取得」を実行した場合は同日）に設定してください。'
    : guideCode === 'lawson'
      ? 'ローチケは販売中公演を取得してから連動するため、販売開始前には設定できません。販売開始後、次回の取得完了後（通常は翌朝。管理者が「今すぐ取得」を実行した場合は同日）に設定してください。'
      : '取得済みデータから同日の公演を選び、券種を確認して保存します。';
  const toast = useToast() as unknown as ToastApi;
  const [data, setData] = useState<EplusCandidateResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [connectionId, setConnectionId] = useState('');
  const [performanceId, setPerformanceId] = useState('');
  const [externalPerformanceId, setExternalPerformanceId] = useState('');
  const [mappingTargets, setMappingTargets] = useState<Record<string, string>>({});
  const [performanceTargets, setPerformanceTargets] = useState<Record<string, string>>({});
  const [capacities, setCapacities] = useState<Record<string, string>>({});
  const [siteFee, setSiteFee] = useState('0');
  const [scheduleMode, setScheduleMode] = useState<'event' | 'custom'>('event');
  const [salesStartAt, setSalesStartAt] = useState('');
  const [salesEndAt, setSalesEndAt] = useState('');
  const [completionMessage, setCompletionMessage] = useState('');
  const [expandedCapacity, setExpandedCapacity] = useState<Record<string, boolean>>({});

  const load = async () => {
    setLoading(true);
    try {
      const next = eventUuid
        ? await listAccountPlayguidePerformanceCandidates(guideCode, eventUuid)
        : await listAvailableAccountPlayguidePerformanceCandidates(guideCode, { performances: eventPerformances });
      setData(next);
      const nextConnectionId = connectionId || String(next.connections[0]?.connectionId || '');
      const nextPerformanceId = performanceId || (next.performances[0]
        ? performanceKey(next.performances[0])
        : '');
      setConnectionId(nextConnectionId);
      setPerformanceId(nextPerformanceId);
      const connection = next.connections.find((item) => String(item.connectionId) === nextConnectionId);
      setSiteFee(String(connection?.defaultSiteFee || 0));
    } catch (error: any) {
      toast.error(error?.message || `${guideLabel}の同日公演候補を取得できませんでした。`);
    } finally {
      setLoading(false);
    }
  };

  const draftPerformanceKey = JSON.stringify(eventPerformances);

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventUuid, draftPerformanceKey, guideCode]);

  const selectedPerformance = data?.performances.find(
    (item) => performanceKey(item) === performanceId
  ) || null;
  const availableCandidates = (selectedPerformance?.candidates || []).filter(
    (candidate) => !connectionId || String(candidate.connectionId) === connectionId
  );
  const selectedCandidate: EplusPerformanceCandidate | null = availableCandidates.find(
    (candidate) => String(candidate.externalPerformanceId) === externalPerformanceId
  ) || null;
  const performanceOptions = data?.performances || [];
  const multiPerformance = performanceScoped && performanceOptions.length > 1;
  const ticketsForScope = (scopeKey: string) => eventTickets.filter((ticket) => {
    if (scopeKey === 'event') return ticket.eventPerformanceId == null && ticket.eventPerformanceIndex == null;
    if (scopeKey.startsWith('id:')) return Number(ticket.eventPerformanceId || 0) === Number(scopeKey.slice(3));
    if (scopeKey.startsWith('index:')) return Number(ticket.eventPerformanceIndex ?? -1) === Number(scopeKey.slice(6));
    return false;
  });

  const selectCandidate = (value: string) => {
    setCompletionMessage('');
    setExpandedCapacity({});
    setExternalPerformanceId(value);
    const candidate = availableCandidates.find(
      (item) => String(item.externalPerformanceId) === value
    );
    const nextTargets: Record<string, string> = {};
    const nextPerformanceTargets: Record<string, string> = {};
    const nextCapacities: Record<string, string> = {};
    const variantCounts = new Map<string, number>();
    candidate?.tickets.forEach((ticket) => {
      const suggestedScope = ticket.suggestedPerformanceKey ||
        (multiPerformance ? '' : selectedPerformance ? performanceKey(selectedPerformance) : 'event');
      const scopeKey = performanceScoped ? suggestedScope : 'event';
      nextPerformanceTargets[ticket.externalTicketKey] = scopeKey;
      const key = `${normalizeTicketLabel(ticket.seatType)}:${Number(ticket.sourcePrice || 0)}`;
      variantCounts.set(key, (variantCounts.get(key) || 0) + 1);
    });
    candidate?.tickets.forEach((ticket) => {
      const scopeKey = nextPerformanceTargets[ticket.externalTicketKey] || 'event';
      const performanceTickets = ticketsForScope(scopeKey);
      const suggestedName = receptionScopedTicketName(ticket.seatType, ticket.receptionName);
      let matches = performanceTickets.filter((existing) =>
        normalizeTicketLabel(existing.ticketType) === normalizeTicketLabel(suggestedName) &&
        Number(existing.price) === Number(ticket.sourcePrice)
      );
      const identityKey = `${normalizeTicketLabel(ticket.seatType)}:${Number(ticket.sourcePrice || 0)}`;
      if (!matches.length && variantCounts.get(identityKey) === 1) {
        matches = performanceTickets.filter((existing) =>
          normalizeTicketLabel(existing.ticketType) === normalizeTicketLabel(ticket.seatType) &&
          Number(existing.price) === Number(ticket.sourcePrice)
        );
      }
      nextTargets[ticket.externalTicketKey] = matches.length === 1
        ? matches[0].ticketId
          ? `existing:${matches[0].ticketId}`
          : `index:${matches[0].ticketIndex}`
        : 'create';
      nextCapacities[ticket.externalTicketKey] = String(
        ticket.suggestedCapacity != null
          ? ticket.suggestedCapacity
          : matches.length === 1
            ? Math.max(matches[0].quantity, ticket.cumulativeConfirmedCount)
            : ''
      );
    });
    setMappingTargets(nextTargets);
    setPerformanceTargets(nextPerformanceTargets);
    setCapacities(nextCapacities);
  };

  const changePerformanceTarget = (externalTicketKey: string, scopeKey: string) => {
    setPerformanceTargets((current) => ({ ...current, [externalTicketKey]: scopeKey }));
    setMappingTargets((current) => {
      const selected = current[externalTicketKey] || '';
      const selectedId = selected.startsWith('existing:') ? Number(selected.slice(9)) : null;
      const selectedIndex = selected.startsWith('index:') ? Number(selected.slice(6)) : null;
      const remainsValid = ticketsForScope(scopeKey).some((ticket) =>
        selectedId != null ? Number(ticket.ticketId || 0) === selectedId :
          selectedIndex != null ? Number(ticket.ticketIndex ?? -1) === selectedIndex : false
      );
      return remainsValid || ['create', 'ignore', 'auto'].includes(selected)
        ? current
        : { ...current, [externalTicketKey]: 'create' };
    });
  };

  const save = async () => {
    if (!selectedPerformance || !selectedCandidate) {
      toast.error(`Casticart公演と${guideLabel}公演を選択してください。`);
      return;
    }
    if (!selectedCandidate.tickets.length) {
      toast.error(`${guideLabel}公演の券種データはまだ取得されていません。次回取得後に設定してください。`);
      return;
    }
    if (multiPerformance && selectedCandidate.tickets.some(
      (ticket) => !performanceTargets[ticket.externalTicketKey]
    )) {
      toast.error('対象公演を判定できない券種があります。各券種の対象公演を選択してください。');
      return;
    }
    const startIso = scheduleMode === 'custom' ? jstDatetimeLocalToUtcIso(salesStartAt) : null;
    const endIso = scheduleMode === 'custom' ? jstDatetimeLocalToUtcIso(salesEndAt) : null;
    if (scheduleMode === 'custom' && (!startIso || !endIso || startIso > endIso)) {
      toast.error('個別の券売開始日時と終了日時を正しく入力してください。');
      return;
    }
    const ticketMappings = selectedCandidate.tickets.map((ticket) => {
      const target = mappingTargets[ticket.externalTicketKey] || 'auto';
      const targetPerformance = performanceTargets[ticket.externalTicketKey] ||
        (multiPerformance ? '' : selectedPerformance ? performanceKey(selectedPerformance) : 'event');
      const [mode, targetId] = target.split(':');
      const capacityText = capacities[ticket.externalTicketKey]?.trim() || '';
      return {
        externalTicketKey: ticket.externalTicketKey,
        targetMode: (mode === 'existing' || mode === 'index' ? 'existing' : mode) as 'auto' | 'existing' | 'create' | 'ignore',
        casticartTicketId: mode === 'existing' ? Number(targetId) : undefined,
        targetTicketIndex: mode === 'index' ? Number(targetId) : undefined,
        configuredCapacity: mode === 'ignore'
          ? ticket.cumulativeConfirmedCount
          : capacityText
            ? Number(capacityText)
            : Number.NaN,
        targetPerformanceMode: targetPerformance === 'event' ? 'event' as const : 'performance' as const,
        casticartPerformanceId: targetPerformance.startsWith('id:')
          ? Number(targetPerformance.slice(3))
          : undefined,
        targetPerformanceIndex: targetPerformance.startsWith('index:')
          ? Number(targetPerformance.slice(6))
          : undefined,
      };
    });
    if (ticketMappings.some((mapping, index) =>
      !Number.isSafeInteger(mapping.configuredCapacity) ||
      mapping.configuredCapacity < selectedCandidate.tickets[index].cumulativeConfirmedCount
    )) {
      toast.error('外部販売枠を取得できない券種は、上限枚数を手動で入力してください。上限枚数は取得済み販売数以上が必要です。');
      return;
    }
    const draft: EplusSourceDraft = {
      casticartPerformanceIndex: selectedPerformance.performanceId == null
        ? performanceScoped
          ? selectedPerformance.performanceIndex ?? null
          : null
        : null,
      connectionId: selectedCandidate.connectionId,
      externalPerformanceId: selectedCandidate.externalPerformanceId,
      ticketMappings,
      siteFee: Number(siteFee || 0),
      salesScheduleMode: scheduleMode,
      salesStartAt: startIso,
      salesEndAt: endIso,
    };
    if (!eventUuid) {
      onDraftChange?.(draft);
      toast.success(`イベント登録時に保存する${guideLabel}連動へ追加しました`);
      return;
    }
    setSaving(true);
    try {
      await linkAccountPlayguidePerformance(guideCode, eventUuid, {
        casticartPerformanceId: selectedPerformance.performanceId,
        connectionId: draft.connectionId,
        externalPerformanceId: draft.externalPerformanceId,
        ticketMappings: draft.ticketMappings.map((mapping) => ({
          externalTicketKey: mapping.externalTicketKey,
          targetMode: mapping.targetMode,
          casticartTicketId: mapping.casticartTicketId,
          configuredCapacity: mapping.configuredCapacity,
          targetPerformanceMode: mapping.targetPerformanceMode,
          casticartPerformanceId: mapping.casticartPerformanceId,
        })),
        siteFee: draft.siteFee,
        salesScheduleMode: draft.salesScheduleMode,
        salesStartAt: draft.salesStartAt,
        salesEndAt: draft.salesEndAt,
      });
      toast.success(`${guideLabel}公演を連動し、取得済みの券売履歴を反映しました`);
      setCompletionMessage(`${guideLabel}の公演・券種の連動設定が完了しました。`);
      setExternalPerformanceId('');
      await load();
      await onSaved();
    } catch (error: any) {
      toast.error(error?.message || `${guideLabel}公演を連動できませんでした。`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="playguide-ticket-source__setup">
      <div className="playguide-ticket-source__setup-title">
        <h4>{guideLabel}を設定</h4>
        <p>{guideTimingNotice}</p>
      </div>
      {completionMessage ? <p className="playguide-ticket-source__completion is-complete" role="status">{completionMessage}</p> : null}
      {loading ? <p className="playguide-ticket-source__state">同日公演を読み込んでいます…</p>
        : !data?.connections.length ? <p className="playguide-ticket-source__empty">現在利用できる{guideLabel}アカウントはありません。プレイガイドアカウントを管理する担当者へ確認してください。</p>
        : !data.performances.length ? <p className="playguide-ticket-source__empty">先にイベントの公演日程を登録してください。</p>
        : <>
          <div className={`playguide-ticket-source__organization-step${data.performances.length === 1 ? ' is-single-performance' : ''}`}>
            <label>{guideLabel}アカウント<Select value={connectionId} onChange={(event: any) => {
              const value = event.target.value;
              setConnectionId(value);
              setExternalPerformanceId('');
              const selected = data.connections.find((item) => String(item.connectionId) === value);
              setSiteFee(String(selected?.defaultSiteFee || 0));
            }}>{data.connections.map((connection) => <option key={connection.connectionId} value={connection.connectionId}>{connection.label}</option>)}</Select></label>
            {data.performances.length > 1 ? <label>Casticart公演<Select value={performanceId} onChange={(event: any) => {
              setPerformanceId(event.target.value);
              setExternalPerformanceId('');
            }}>{data.performances.map((performance) => <option key={performanceKey(performance)} value={performanceKey(performance)}>{performance.performanceDate} {performance.startTime} / {performance.performanceName}</option>)}</Select></label> : null}
          </div>
          <label>同日の{guideLabel}公演<Select value={externalPerformanceId} onChange={(event: any) => selectCandidate(event.target.value)}>
            <option value="">選択してください</option>
            {availableCandidates.map((candidate) => <option key={candidate.externalPerformanceId} value={candidate.externalPerformanceId} disabled={Boolean(candidate.linkedSourceId)}>{candidate.eventName} / {candidate.venueName}{candidate.linkedSourceId ? '（連動済み）' : ''}</option>)}
          </Select></label>
          {!availableCandidates.length ? <p className="playguide-ticket-source__empty">この日の取得済み公演はありません。販売開始後、次回の自動取得完了後（通常は翌朝）、または管理者が外部チケット連携で「今すぐ取得」を実行した後に候補へ表示されます。</p> : null}
          {selectedCandidate ? <div className="playguide-ticket-source__preview">
            {!selectedCandidate.tickets.length ? <p className="playguide-ticket-source__empty">公演は取得済みですが、販売実績表に券種データがありません。次回取得後に券種を設定できます。</p> : null}
            {selectedCandidate.tickets.some((ticket) => ticket.suggestedCapacity == null) ? (
              <p className="playguide-ticket-source__empty">
                外部サイトから販売枠を取得できなかった券種は、上限枚数を手動で入力してください。販売数を上限として自動設定することはありません。
              </p>
            ) : (
              <p className="playguide-ticket-source__empty">外部サイトの販売枠を上限枚数へ入力しました。必要に応じて変更できます。</p>
            )}
            {(() => {
              const autoMatchedCount = selectedCandidate.tickets.filter((ticket) => {
                const value = mappingTargets[ticket.externalTicketKey] || 'auto';
                return value.startsWith('existing:') || value.startsWith('index:');
              }).length;
              const attentionCount = selectedCandidate.tickets.filter((ticket) => {
                const needsPerformance = multiPerformance && !performanceTargets[ticket.externalTicketKey];
                return needsPerformance || !capacities[ticket.externalTicketKey];
              }).length;
              const orderedTickets = [...selectedCandidate.tickets].sort((left, right) =>
                Number(left.suggestedCapacity != null) - Number(right.suggestedCapacity != null)
              );
              return <>
              <p className="playguide-ticket-source__map-summary" role="status">{autoMatchedCount >= selectedCandidate.tickets.length
                ? `${selectedCandidate.tickets.length}件すべてを名称・金額から自動一致しました。`
                : `${selectedCandidate.tickets.length}件中${autoMatchedCount}件を名称・金額から自動一致しました。${attentionCount ? `残り${attentionCount}件を確認してください。` : ''}`}</p>
              <div className="playguide-ticket-source__map-list">{orderedTickets.map((ticket) => {
              const scopeKey = performanceTargets[ticket.externalTicketKey] ||
                (multiPerformance ? '' : selectedPerformance ? performanceKey(selectedPerformance) : 'event');
              const scopedTickets = ticketsForScope(scopeKey);
              const scopedName = receptionScopedTicketName(ticket.seatType, ticket.receptionName);
              const target = mappingTargets[ticket.externalTicketKey] || 'auto';
              const isMapped = target.startsWith('existing:') || target.startsWith('index:');
              const needsPerformance = multiPerformance && !performanceTargets[ticket.externalTicketKey];
              const needsCapacity = !capacities[ticket.externalTicketKey];
              const attention = needsPerformance || needsCapacity;
              const capacityOpen = needsCapacity || Boolean(expandedCapacity[ticket.externalTicketKey]);
              return <article key={ticket.externalTicketKey} className={`playguide-ticket-source__map-card${attention ? ' is-attention' : ''}`}>
                <div className="playguide-ticket-source__map-card-head"><div><strong>{scopedName}</strong>{isMapped ? <span className="playguide-ticket-source__map-tag is-auto">自動一致</span> : null}{attention ? <span className="playguide-ticket-source__map-tag is-attention">要確認</span> : null}</div><small>{yen.format(ticket.sourcePrice)}</small></div>
                <p className="playguide-ticket-source__map-card-sub">{ticket.seatType}{ticket.receptionName ? ` ・ 受付: ${ticket.receptionName}` : ''} ・ 取得済み {ticket.cumulativeConfirmedCount}枚</p>
                <div className="playguide-ticket-source__map-card-fields">
                  {multiPerformance ? <label>対象公演<Select aria-label={`${ticket.seatType}の対象公演`} value={scopeKey} onChange={(event: any) => changePerformanceTarget(ticket.externalTicketKey, event.target.value)}><option value="">対象公演を選択</option><option value="event">イベント全体（通し券など）</option>{performanceOptions.map((performance) => { const key = performanceKey(performance); const suggested = ticket.suggestedPerformanceKey === key ? '（自動候補）' : ''; return <option key={key} value={key}>{performance.performanceDate} {performance.startTime} / {performance.performanceName}{suggested}</option>; })}</Select>{ticket.performanceSuggestionReason === 'unresolved' ? <small>候補を判定できないため選択してください</small> : null}</label> : null}
                  <label>対応先<Select aria-label={`${scopedName}の対応先`} value={target} onChange={(event: any) => setMappingTargets((current) => ({ ...current, [ticket.externalTicketKey]: event.target.value }))}><option value="auto">受付を含む名称・金額が一致すれば自動</option><option value="create">「{scopedName}」として追加</option><option value="ignore">未紐付けのまま保存</option>{scopedTickets.map((existing) => { const value = existing.ticketId ? `existing:${existing.ticketId}` : `index:${existing.ticketIndex}`; const scopeLabel = existing.eventPerformanceId == null ? 'イベント全体 / ' : ''; return <option key={value} value={value}>{scopeLabel}{existing.ticketType} / {yen.format(existing.price)}</option>; })}</Select></label>
                </div>
                <div className="playguide-ticket-source__map-card-capacity">{capacityOpen ? <label>上限枚数<input aria-label={`${scopedName}の上限枚数`} type="number" min={ticket.cumulativeConfirmedCount} placeholder="手動入力" value={capacities[ticket.externalTicketKey] || ''} onChange={(event) => setCapacities((current) => ({ ...current, [ticket.externalTicketKey]: event.target.value }))} /></label> : <button type="button" className="playguide-ticket-source__cap-toggle" onClick={() => setExpandedCapacity((current) => ({ ...current, [ticket.externalTicketKey]: true }))}>上限を調整（現在 {capacities[ticket.externalTicketKey]}枚）</button>}</div>
              </article>;
            })}</div></>;
            })()}
            <div className="playguide-ticket-source__schedule-editor">
              <label>サイト手数料（%）<input type="number" min="0" max="100" step="0.001" value={siteFee} onChange={(event) => setSiteFee(event.target.value)} /></label>
              <label>券売期間<Select value={scheduleMode} onChange={(event: any) => setScheduleMode(event.target.value)}><option value="event">券売予定に合わせる</option><option value="custom">個別で設定する</option></Select></label>
              {scheduleMode === 'custom' ? <div className="playguide-ticket-source__schedule-dates"><label>開始日時<input type="datetime-local" value={salesStartAt} onChange={(event) => setSalesStartAt(event.target.value)} /></label><label>終了日時<input type="datetime-local" value={salesEndAt} onChange={(event) => setSalesEndAt(event.target.value)} /></label></div> : null}
            </div>
            <div className="playguide-ticket-source__preview-actions"><DsButton variant="secondary" onClick={() => selectCandidate('')}>公演を選び直す</DsButton><DsButton loading={saving} disabled={!selectedCandidate.tickets.length} onClick={save}>{eventUuid ? 'このサイトの連動を保存' : '登録内容へ追加'}</DsButton></div>
          </div> : null}
        </>}
    </div>
  );
}
